import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { faCircleCheck, faXmark, faCircleHalfStroke } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";

type CellValue = "yes" | "no" | "partial";

type Row = {
	feature: string;
	values: CellValue[];
};

const renderCell = (value: CellValue): ReactNode => {
	if (value === "yes") {
		return <Icon className="w-5 h-5 text-[#2AA216]" icon={faCircleCheck} />;
	}

	if (value === "partial") {
		return <Icon className="w-5 h-5 text-[#E0A100]" icon={faCircleHalfStroke} />;
	}

	return <Icon className="w-4 h-4 text-[#C4C7D0]" icon={faXmark} />;
};

export default function LandingComparisonSection() {
	const { t } = useTranslation();
	const ts = (key: string) => t(`questionnaire.landingComparison.${key}`);

	const columns = t("questionnaire.landingComparison.columns", {
		returnObjects: true,
	}) as string[];
	const rows = t("questionnaire.landingComparison.rows", {
		returnObjects: true,
	}) as Row[];

	// The last column is always NIS360 (highlighted).
	const lastIndex = columns.length - 1;

	return (
		<section className="w-full px-4 md:px-0 bg-[#F7F8FB]">
			<div className="w-full max-w-[1420px] mx-auto py-16 sm:py-24 px-4 md:px-16 flex flex-col gap-12">
				<div className="flex flex-col items-center gap-4 text-center max-w-[820px] mx-auto">
					<span className="font-urbanist text-[13px] font-bold uppercase tracking-[0.12em] text-[#4680FC] bg-[#F0F4FF] border border-[#D8E4FF] rounded-full px-4 py-1.5">
						{ts("kicker")}
					</span>
					<h2 className="font-urbanist text-[34px] md:text-[40px] font-semibold leading-[120%] text-[#1D243C]">
						{ts("title")}
					</h2>
					<p className="font-urbanist text-[18px] font-normal leading-[150%] text-[#626373]">
						{ts("subtitle")}
					</p>
				</div>

				<div className="w-full overflow-x-auto">
					<table className="w-full min-w-[720px] border-separate border-spacing-0">
						<thead>
							<tr>
								<th className="text-left align-bottom p-4 font-urbanist text-[14px] font-semibold text-[#626373]">
									{ts("featureHeader")}
								</th>
								{columns.map((col, index) => {
									const isLast = index === lastIndex;

									return (
										<th
											key={col}
											className={`p-4 text-center align-bottom font-urbanist text-[15px] font-bold ${
												isLast ? "text-white bg-[#4680FC] rounded-t-2xl" : "text-[#1D243C]"
											}`}
										>
											{isLast && (
												<span className="block mb-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-white/80">
													{ts("highlightLabel")}
												</span>
											)}
											{col}
										</th>
									);
								})}
							</tr>
						</thead>
						<tbody>
							{rows.map((row, rowIndex) => {
								const isLastRow = rowIndex === rows.length - 1;

								return (
									<tr key={row.feature}>
										<td className="p-4 font-urbanist text-[15px] leading-[150%] text-[#1D243C] border-t border-[#E6E6EC]">
											{row.feature}
										</td>
										{row.values.map((value, colIndex) => {
											const isLast = colIndex === lastIndex;

											return (
												<td
													key={colIndex}
													className={`p-4 text-center border-t border-[#E6E6EC] ${
														isLast ? "bg-[#EDF3FF]" : ""
													} ${isLast && isLastRow ? "rounded-b-2xl" : ""}`}
												>
													<span className="flex justify-center">{renderCell(value)}</span>
												</td>
											);
										})}
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</div>
		</section>
	);
}
